// helpers.js
// Shared row/profit math — single source of truth for "sold" / "unsold" /
// "total profit", used by OverviewTab, AnalyticsTab and TradedTab so their
// numbers can't drift apart from independently hand-rolled copies.

// The item-picture lookup and the sticker index both answer from Maps built
// once per skins database (skinIndex.js) instead of scanning all 40,000 entries
// per call, which is what used to make a page of cards take most of a second to
// draw. CONDITION_MAP moved there too, since that lookup is its only consumer.
// All three are re-exported unchanged so every existing
// `import { ... } from './helpers'` keeps resolving.
// Imported for use further down this file, then re-exported: `export { x } from`
// alone would forward the name without binding it locally.
import { CONDITION_MAP, findSkinImage } from './skinIndex';
export { CONDITION_MAP, findSkinImage, buildStickerIndex } from './skinIndex';

// Wear tier -> short class suffix, for the green->red exterior ramp. Rows carry
// either spelling depending on where they came from (the picker writes "Field
// Tested", CSFloat and market_hash_name give "Field-Tested"), so the lookup
// normalises the hyphen and case rather than listing both forms.
const WEAR_CLASS = {
  'factory new':    'fn',
  'minimal wear':   'mw',
  'field tested':   'ft',
  'well worn':      'ww',
  'battle scarred': 'bs',
};

// '' for anything unrecognised (or missing) so the caller's element just keeps
// its inherited colour instead of falling into an arbitrary tier.
export function wearClass(condition) {
  if (!condition) return '';
  return WEAR_CLASS[String(condition).toLowerCase().replace(/-/g, ' ').trim()] || '';
}

// Steam's own CDN icon, as recorded by the extension. Inventory items arrive
// with either spelling depending on which route delivered them.
export function getInventoryIcon(inv) {
  return inv?.iconUrl || inv?.icon_url || null;
}

// Picture for a tracker row. The skins database only knows items that existed
// when the snapshot was taken, so newly released ones would show a blank tile.
// Both extension paths stamp the Steam icon onto the row at add time, and the
// synced inventory still holds it for rows added before that, so try those
// first and only then fall back to the database.
export function getRowImage(row, skinsData, inventoryMap) {
  if (!row) return null;
  const stamped = getInventoryIcon(row);
  if (stamped) return stamped;
  const inv = row.assetId ? inventoryMap?.get?.(row.assetId) : null;
  return getInventoryIcon(inv) || findSkinImage(skinsData, row.skin?.value, row.condition?.value);
}

// A row counts as "sold" once it has a sellPrice. A row without a skin name
// isn't a real position, so it's excluded from "unsold" (canonical definition —
// previously OverviewTab and AnalyticsTab disagreed on this).
export function getSoldRows(rows) {
  return (rows || []).filter(r => parseFloat(r.sellPrice) > 0);
}

/**
 * Traded away — the item left in a swap, so it is not yours any more.
 *
 * THE ONE DEFINITION. Five files used to spell this out by hand and two of them
 * left it out entirely, which is how a knife ended up drawn in Live and in
 * Traded at the same time, its cost basis counted once on its own row and again
 * across the rows the trade created.
 *
 * Both halves are required:
 *   tradeId          Steam saw something leave.
 *   tradedFor.length ...and we know what came back.
 *
 * A tradeId with an EMPTY tradedFor is the amber Resolve state — something left
 * and the app does not yet know what it was, possibly a lend. That row is still
 * a holding and must keep showing up in Live, or an unresolved trade disappears
 * with nowhere left to answer it from.
 *
 * The sellPrice guard keeps a row that was traded and later sold reading as
 * sold: that is where its money actually landed.
 */
export function isTradedAway(row) {
  return !!(row?.tradeId && row.tradedFor?.length > 0) && !(parseFloat(row.sellPrice) > 0);
}

/**
 * What you currently hold — the basis for every portfolio total.
 *
 * Traded-away rows are excluded. They keep their buyPrice as a historical
 * record, but handleTradeItem has already allocated that basis across the rows
 * the trade produced, so counting the offered row here would count the same
 * money twice and report unrealized P/L on an item you do not own. The row
 * itself is not lost: it is the left-hand side of its trade on the Traded tab,
 * and buildLineage in tradeLineage.js walks the chain from either end.
 *
 * LENT ROWS ARE KEPT, and the difference from traded-away is the whole point.
 * An item out with a friend is still yours: the capital is still tied up in it
 * and the market still moves it, so dropping it here would make lending
 * something look like losing money. It leaves the Live and Vault LISTS, because
 * those answer "what is in my hands" — see isFlipRow in investments.js — but it
 * never leaves your portfolio.
 */
export function getUnsoldRows(rows) {
  return (rows || []).filter(r => !(parseFloat(r.sellPrice) > 0) && r.skin?.value && !isTradedAway(r));
}

// ── Which inventory items are already spoken for ───────────────────────────
//
// WHY THIS EXISTS
// "Send to Live" used to be gated on assetId alone: an inventory item was
// darkened once some row carried its exact asset id. That is right for an item
// you added yourself, and blind to the other way items reach Live.
//
// A trade puts its received items straight into Live (autoLinkTrades on the
// server), and those rows usually carry NO assetId — the item is still under
// trade protection when the trade is read, so Steam has not yet told anyone
// which copy it is. The very same item then shows up in the Locker looking
// untracked, gets sent to Live a second time, and one item is now two rows: the
// trade row with its real cost basis, and a fresh one priced at today's market.
//
// So the question the grid has to answer is not "does a row carry this asset
// id" but "do the rows already account for this item". That is the same rule
// planReceivedRows follows on the server (see server/services/receivedRows.js):
// ROWS MAY NOT OUTNUMBER THE ITEMS THEY DESCRIBE.
//
// HOW IT IS COUNTED, AND WHY IT IS COUNTED THAT WAY
//   1. An asset id on a row is proof — that row IS that copy, so the copy is
//      covered and nothing else needs deciding.
//   2. What is left is matched by name and wear, and only rows carrying no
//      asset id at all may claim an item that way. A row that names a DIFFERENT
//      copy has already been spent in step 1; letting it also cover a copy it
//      does not name is what would darken the AK you still hold because the AK
//      you sold last week has a row.
//
// Sold, traded-away and lent rows never claim: their item is not in your hands,
// so an item of that name sitting in the inventory is a different one.
//
// The counting direction is deliberate. Covering one item too many costs a
// click — the user edits the row instead of adding it. Covering one too few
// costs a duplicate row that quietly double-counts an item in every total in
// the app. So where it cannot tell, it covers.
const WEAR_SUFFIX_RE =
  /\s*\((Factory New|Minimal Wear|Field[- ]Tested|Well[- ]Worn|Battle[- ]Scarred)\)\s*$/;

// Steam's hyphenated wear spelling -> the spelling rows store. Both forms are
// listed so re-normalizing an already-normalized value is harmless.
const ROW_WEAR = {
  'Factory New':    'Factory New',
  'Minimal Wear':   'Minimal Wear',
  'Field-Tested':   'Field Tested',
  'Well-Worn':      'Well Worn',
  'Battle-Scarred': 'Battle Scarred',
  'Field Tested':   'Field Tested',
  'Well Worn':      'Well Worn',
  'Battle Scarred': 'Battle Scarred',
};

/** "AK-47 | Redline (Field-Tested)" -> "AK-47 | Redline" */
function stripWear(name) {
  return String(name || '').replace(WEAR_SUFFIX_RE, '').trim();
}

function wearOf(exterior, marketHashName) {
  if (exterior && ROW_WEAR[exterior]) return ROW_WEAR[exterior];
  const m = WEAR_SUFFIX_RE.exec(String(marketHashName || ''));
  return m ? (ROW_WEAR[m[1]] ?? m[1].replace(/-/g, ' ')) : '';
}

/** Name and wear of an inventory item, however that item spells them. */
export function inventoryItemKey(item) {
  const mhn = item?.marketHashName || item?.market_hash_name || '';
  return `${stripWear(mhn)}|${wearOf(item?.exterior, mhn)}`;
}

/** The same key, off a tracker row. */
export function rowItemKey(row) {
  const cond = row?.condition?.value;
  return `${stripWear(row?.skin?.value)}|${ROW_WEAR[cond] || cond || ''}`;
}

/** A row that speaks for something currently in your hands. */
function claimsAnItem(row) {
  return !!row?.skin?.value
    && !(parseFloat(row.sellPrice) > 0)
    && !isTradedAway(row)
    && !row.lend;
}

/**
 * Asset ids of inventory items the tracker already holds a row for.
 *
 * Drop-in replacement for `new Set(rows.map(r => r.assetId))` — it still covers
 * every item whose id a row names, and additionally covers items that a row
 * describes without naming.
 *
 * @param {Array} rows            tracker rows
 * @param {...Array} itemLists    inventory items to judge (synced inventory,
 *                                new appearances, storage — any order, and the
 *                                same item may appear in more than one)
 * @returns {Set<string>} asset ids that must not be sent to Live again
 */
export function coveredAssetIds(rows, ...itemLists) {
  const covered = new Set();

  // Every id any row names, whatever that row's state. A sold row's asset id
  // still names a copy that is not up for grabs.
  const named = new Set();
  (rows || []).forEach(r => { if (r?.assetId) named.add(String(r.assetId)); });

  const seen = new Set();
  const unclaimed = [];
  for (const list of itemLists) {
    for (const item of list || []) {
      const id = item?.assetId;
      if (!id || seen.has(String(id))) continue;
      seen.add(String(id));
      if (named.has(String(id))) covered.add(id);
      else unclaimed.push(item);
    }
  }
  if (!unclaimed.length) return covered;

  // Rows describing a held item without naming which copy. These are what a
  // trade leaves behind, and what a hand-typed row has always been.
  const loose = new Map();
  (rows || []).forEach(r => {
    if (!r || r.assetId || !claimsAnItem(r)) return;
    const key = rowItemKey(r);
    loose.set(key, (loose.get(key) || 0) + 1);
  });
  if (!loose.size) return covered;

  for (const item of unclaimed) {
    const key = inventoryItemKey(item);
    const left = loose.get(key) || 0;
    if (left <= 0) continue;
    loose.set(key, left - 1);
    covered.add(item.assetId);
  }

  return covered;
}

export function getIncludedSold(rows, isExcluded) {
  const sold = getSoldRows(rows);
  return isExcluded ? sold.filter(r => !isExcluded(r)) : sold;
}

export function getTotalProfit(rows, isExcluded) {
  return getIncludedSold(rows, isExcluded).reduce((s, r) => s + (parseFloat(r.profit) || 0), 0);
}

// Steam trade locks expire 7 days after the item is received, but the
// tradeProtected flag on a row (or on synced inventory) is only ever as fresh
// as the last extension sync. Without an expiry check an item sits "locked"
// forever if the user never re-syncs, so:
//   1. a stored unlock date that has passed means the lock is over, and
//   2. when no unlock date was recorded, fall back to 7 days after the row was
//      registered, which is when the item entered the tracker.
// Returns daysLeft = null only when the flag is set and there is no date at
// all to reason from.
//
// The inventory copy outranks the row. A row's lock fields are a snapshot
// taken the moment it was created off the inventory, and never refreshed; the
// inventory is re-read on every sync, and the server fills in its unlock date
// from owner_descriptions when the column is empty (tradeProtectionDate.js).
// Reading the row first is what let Live show a different date than the
// Locker for the very same item. The row is only the answer when the item is
// not in the synced inventory at all.
export const TRADE_LOCK_DAYS = 7;

export function getTradeLock(row, inv) {
  const source  = inv ?? row;
  const flagged = source?.tradeProtected ?? false;
  if (!flagged) return { locked: false, until: null, daysLeft: 0, inferred: false };

  const raw = source?.tradeProtectedUntil ?? (inv ? row?.tradeProtectedUntil : null) ?? null;
  let until = raw ? new Date(raw) : null;
  if (until && Number.isNaN(until.getTime())) until = null;

  let inferred = false;
  if (!until && row?.timestamp) {
    const registered = new Date(row.timestamp);
    if (!Number.isNaN(registered.getTime())) {
      until = new Date(registered.getTime() + TRADE_LOCK_DAYS * 86400000);
      inferred = true;
    }
  }

  if (!until) return { locked: true, until: null, daysLeft: null, inferred: false };

  const msLeft = until.getTime() - Date.now();
  if (msLeft <= 0) return { locked: false, until, daysLeft: 0, inferred };
  return { locked: true, until, daysLeft: Math.ceil(msLeft / 86400000), inferred };
}

// Wear tiers, in float order. `value` is the internal form stored on rows;
// `max` is the upper float bound, used to place the marker on wear bars.
export const COND_TIERS = [
  { value: 'Factory New',    short: 'FN', max: 0.07 },
  { value: 'Minimal Wear',   short: 'MW', max: 0.15 },
  { value: 'Field Tested',   short: 'FT', max: 0.38 },
  { value: 'Well Worn',      short: 'WW', max: 0.45 },
  { value: 'Battle Scarred', short: 'BS', max: 1.00 },
];

// Net profit on a sale, after the marketplace's sales + withdrawal fees.
// Returns '' while there is no sell price, which callers read as "still held".
export function calcProfit(buyPrice, sellPrice, siteFee) {
  const buy  = parseFloat(buyPrice)  || 0;
  const sell = parseFloat(sellPrice) || 0;
  if (!sell) return '';
  const fees = sell * ((siteFee?.salesFee || 0) + (siteFee?.withdrawalFee || 0));
  return (sell - fees - buy).toFixed(2);
}

export function getLivePrice(livePrices, row) {
  if (!livePrices || !row.skin?.value) return null;
  if (row.condition?.value) {
    const cond = CONDITION_MAP[row.condition.value] || row.condition.value;
    return livePrices[`${row.skin.value} (${cond})`] || null;
  }
  return livePrices[row.skin.value] || null;
}

export function getBuffPrice(livePrices, row) {
  return getLivePrice(livePrices, row)?.starting_at?.price ?? null;
}

// Per-row P&L: realized profit once sold, mark-to-market gain while unsold.
export function getRowPnl(row, livePrices) {
  if (parseFloat(row.sellPrice) > 0) return parseFloat(row.profit) || 0;
  const market = getBuffPrice(livePrices, row);
  const buy = parseFloat(row.buyPrice) || 0;
  return market != null ? market - buy : 0;
}

// ── Crafts ─────────────────────────────────────────────────
// A craft is an item with stickers applied. Two numbers describe one: what the
// bare skin is worth, and what the stickers on it are worth. Buff quotes both,
// stickers under their own market name.

/** True for anything wearing at least one named sticker. */
export function isCraft(row) {
  return (row?.stickers || []).some(s => s?.name);
}

/**
 * Market value of the stickers on a row.
 *   total    summed price of the ones Buff knows
 *   count    stickers applied
 *   priced   how many of those had a price
 *   complete whether every sticker was priced — a partial total makes any
 *            percentage built on it an understatement, so callers can say so
 */
export function getStickerValue(row, livePrices) {
  const stickers = (row?.stickers || []).filter(s => s?.name);
  let total = 0, priced = 0;
  for (const s of stickers) {
    const p = livePrices?.[`Sticker | ${s.name}`]?.starting_at?.price;
    if (p != null) { total += p; priced++; }
  }
  return { total, count: stickers.length, priced, complete: stickers.length > 0 && priced === stickers.length };
}

// ── Sticker pictures ───────────────────────────────────────
// Stickers reach the app by two routes carrying different amounts of detail.
//
// From a synced Steam inventory they arrive complete: name, CDN image, wear.
// From the trade-history scraper they arrive as a bare `{ name }`, because the
// only place stickers appear on that page is as text inside the item's
// description blob. A strip of five numbered but empty boxes is a poor way to
// show a craft, and the bundled skins database happens to know all 10k of them.
//
// So the picture is looked up by name and filled in. Everything else about the
// sticker (price, and the strip itself) already works off the name.

// buildStickerIndex moved to skinIndex.js, where it is now one view onto the
// single pass that also answers item picture lookups, and is re-exported from
// the top of this file. Callers are unchanged.

/**
 * Stickers with a picture attached wherever one was missing.
 *
 * An image the item already carries always wins: that one came off the real
 * item on Steam, so it reflects the actual sticker, while the database entry is
 * only the catalogue art for that name.
 */
export function withStickerImages(stickers, index) {
  if (!stickers?.length) return stickers || [];
  if (!index?.size) return stickers;
  return stickers.map(s => {
    if (!s?.name || s.image) return s;
    const image = index.get(s.name);
    return image ? { ...s, image } : s;
  });
}

/**
 * Sticker percentage — the craft market's own unit of "how much did you pay for
 * the stickers".
 *
 *   SP = (what you paid − what the bare skin costs) ÷ what the stickers cost
 *
 * A $10 skin carrying $100 of stickers, bought for $20: the $10 over the skin's
 * own price bought $100 of stickers, so you paid 10% SP. It is the only figure
 * that compares two crafts fairly, because it is independent of both the skin
 * underneath and the size of the sticker job.
 *
 * `paid` defaults to the row's own buy price, so an item priced on its own and
 * one priced as part of a lot are read exactly the same way — the lot's split
 * has already written each item's share into buyPrice by the time this runs.
 *
 * Returns null when the row is not a craft or when a price is missing that would
 * make the answer a guess.
 */
export function getStickerPct(row, livePrices, paid) {
  if (!isCraft(row)) return null;
  const spent = paid != null ? Number(paid) : parseFloat(row?.buyPrice);
  if (!Number.isFinite(spent)) return null;

  const base = getBuffPrice(livePrices, row);
  if (base == null) return null;

  const stickers = getStickerValue(row, livePrices);
  if (stickers.total <= 0) return null;

  const overpay = spent - base;
  return {
    sp:       overpay / stickers.total * 100,
    paid:     spent,
    base,
    stickers: stickers.total,
    overpay,
    complete: stickers.complete,
  };
}

/**
 * The badge text and the hover breakdown for a sticker percentage, so every
 * place that shows one says it the same way.
 */
export function describeStickerPct(info) {
  if (!info) return null;
  const round = v => (Math.abs(v) >= 100 ? Math.round(v) : Math.round(v * 10) / 10);
  const cash  = v => `$${v.toFixed(2)}`;
  const label = `SP ${round(info.sp)}%${info.complete ? '' : '+'}`;
  const title = [
    `Paid ${cash(info.paid)} for a ${cash(info.base)} skin`,
    `${info.overpay >= 0 ? 'Over' : 'Under'} the skin by ${cash(Math.abs(info.overpay))}`,
    `Stickers worth ${cash(info.stickers)}${info.complete ? '' : ' (some not priced, so the real SP is lower)'}`,
    `Sticker percentage paid: ${round(info.sp)}%`,
  ].join('\n');
  return { label, title };
}

// ── Trade boot ─────────────────────────────────────────────
// The cash side of an item-for-item trade. Real trades are rarely even: you
// give a knife and take back a cheaper one plus $150, or you add $200 to your
// item to get something better. Both happen, so boot is a SIGNED number on the
// offered row.
//
//   positive  you received cash on top of the item
//   negative  you paid cash on top of the item
//
// WHY SIGNED RATHER THAN TWO FIELDS
// Every place that uses boot wants the net effect, and a pair of fields makes
// every one of those places responsible for combining them correctly. One
// signed number cannot be combined wrongly. The direction is a detail of the
// input control, not of the data.
//
// The old shape was `cashReceived`, a positive-only string, and rows saved
// before boot existed still carry it. Read through here and both shapes work,
// which is why nothing reads either field directly.

/** Signed boot in dollars: positive received, negative paid. */
export function getTradeBoot(row) {
  const boot = row?.boot;
  if (boot && Number.isFinite(Number(boot.amount))) return Number(boot.amount);
  // Legacy rows: a positive-only "cash also received".
  return parseFloat(row?.cashReceived) || 0;
}

/** Which wallet the boot moved through, when it was recorded. */
export function getTradeBootSource(row) {
  return row?.boot?.source || null;
}

// ── Valuing a trade ────────────────────────────────────────
// WHY A PER-ITEM P/L IS NOT SHOWN ON A TRADE
// Every item received in a trade inherits a SLICE of the offered item's cost
// basis. The slice is an accounting device, not a price anyone paid: the client
// splits by market value, and the server — which has no price cache — splits
// evenly, so five items received for a $620 knife each get a $124 "cost".
//
// Print a P/L against that and the card starts lying in a very confident voice.
// A $76 agent reads as -$47 and a $581 knife as +$457, as though one leg of the
// trade were a disaster and the other a windfall, when in truth you made one
// decision about one basket and it is up $186. Nobody paid $124 for the agent.
//
// So the trade view shows PRICES — what each item is worth, or what it actually
// sold for — and leaves the profit where the decision was made: on the trade as
// a whole. The per-item basis still exists and still does its real job, which is
// making a later sale net against real cost instead of booking the whole sale
// price as profit.

/**
 * What one received item is worth, in money that is really its own.
 *
 * Sold rows report net proceeds (basis + stored profit, so fees are already
 * out) — the cash that landed. Held rows report the market. `value` is null
 * when nothing can price it, which the caller must show rather than treat as 0.
 */
export function receivedValue(row, livePrices) {
  if (parseFloat(row?.sellPrice) > 0) {
    const buy = parseFloat(row.buyPrice) || 0;
    return {
      sold: true,
      value: buy + (parseFloat(row.profit) || 0),
      gross: parseFloat(row.sellPrice) || 0,
    };
  }
  return { sold: false, value: getBuffPrice(livePrices, row), gross: null };
}

/**
 * The whole trade, valued as one decision, at today's prices.
 *
 * Named "...Now" to sit beside the Traded tab's own archive figure, which asks
 * the different question of what both sides were worth on the day it happened.
 *
 *   gave       cost basis of the item handed over
 *   boot       cash on the side, signed: + taken, - added
 *   back       what came back is worth now — sold legs at proceeds, held legs
 *              at market, unpriced legs contributing nothing
 *   potential  back + boot - gave
 *
 * `potential` is the same number the old "Trade P/L" printed, because the
 * per-item slices always sum back to `gave - boot`. What changes is that it is
 * now named for what it is: an unrealized figure that only becomes real profit
 * when the last leg is sold. `settled` says when that has happened.
 *
 * `unpriced` is the honesty valve. A leg with no price feed silently drags the
 * total down, so the caller can say "2 of 5 not priced" instead of showing a
 * loss that is really a gap in the data.
 */
export function valueTradeNow(offered, receivedRows, livePrices) {
  // `offered` may be one row or every row given in the trade. A Steam trade can
  // hand over seven items at once, and what came back was paid for by all of
  // them, so the cost of the trade is their sum — reading only the first would
  // report a profit against a fraction of what it actually cost.
  const gaveRows = Array.isArray(offered) ? offered : [offered];
  const gave = gaveRows.reduce((s, r) => s + (parseFloat(r?.buyPrice) || 0), 0);
  // The cash sits on the trade, not on each leg, so it is counted once.
  const boot = getTradeBoot(gaveRows[0]);
  const legs = (receivedRows || []).map(r => receivedValue(r, livePrices));

  const back     = legs.reduce((s, l) => s + (l.value ?? 0), 0);
  const sold     = legs.filter(l => l.sold).length;
  const unpriced = legs.filter(l => !l.sold && l.value == null).length;

  return {
    gave, boot, back, legs,
    potential: back + boot - gave,
    sold, unpriced,
    held: legs.length - sold,
    count: legs.length,
    settled: legs.length > 0 && sold === legs.length,
  };
}

// The cost basis that comes OUT of a trade (what you gave, plus or minus the
// cash) is deliberately not computed here. It is applied at the two moments a
// trade is recorded, in handleTradeItem and in userManager.resolveTradeBoot,
// and a third copy that nothing called would only be somewhere for the rule to
// drift out of step.

// Split `totalCents` across `weights` (proportional, integer cents) using the
// largest-remainder method so parts always sum exactly to the total — same
// algorithm server/routes/groupedPurchases.js uses to split a purchase price
// across items by market value.
export function allocateCents(weights, totalCents) {
  const combined = weights.reduce((s, w) => s + (w > 0 ? w : 0), 0);
  if (combined <= 0) {
    // No usable weights — split evenly.
    const even = Math.floor(totalCents / weights.length);
    const out = weights.map(() => even);
    let remainder = totalCents - even * weights.length;
    for (let i = 0; i < out.length && remainder > 0; i++, remainder--) out[i] += 1;
    return out;
  }
  const raw = weights.map(w => ((w > 0 ? w : 0) / combined) * totalCents);
  const rounded = raw.map(v => Math.round(v));
  const sumRounded = rounded.reduce((s, v) => s + v, 0);
  let remaining = totalCents - sumRounded;
  if (remaining !== 0) {
    const sign = remaining > 0 ? 1 : -1;
    const order = raw
      .map((v, idx) => ({ idx, fraction: v - Math.floor(v) }))
      .sort((a, b) => sign > 0 ? b.fraction - a.fraction : a.fraction - b.fraction);
    for (let i = 0; i < Math.abs(remaining) && i < order.length; i++) {
      rounded[order[i].idx] += sign;
    }
  }
  return rounded;
}
